import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { ArrowLeft, Edit, Trash2, Users, Calendar, MessageSquare, AlertCircle, CheckCircle, RefreshCw, Send } from 'lucide-react'
import { announcementApi } from '../services/api'
import { showError } from '../utils/showError'
import { 
  PageHeader, 
  LoadingSpinner, 
  Button,
  Card,
  CardHeader,
  CardBody,
  FormSelect,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  StatusBadge,
  ConfirmationModal,
  EmptyState,
  DetailPageLayout
} from '../components/common'

const AnnouncementDetail = () => {
  const { announcementId } = useParams()
  const navigate = useNavigate()
  const [announcement, setAnnouncement] = useState(null)
  const [recipients, setRecipients] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [recipientsLoading, setRecipientsLoading] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [recipientFilters, setRecipientFilters] = useState({
    isRead: '',
    isAcknowledged: ''
  })
  const [recipientPage, setRecipientPage] = useState(1)
  const [recipientTotalPages, setRecipientTotalPages] = useState(1)

  useEffect(() => {
    loadAnnouncementDetails()
    loadAnnouncementStats()
  }, [announcementId])

  useEffect(() => {
    if (recipients.length > 0 || recipientPage > 1) {
      loadRecipients()
    }
  }, [recipientFilters, recipientPage])

  const loadAnnouncementDetails = async () => {
    try {
      setLoading(true)
      
      const response = await announcementApi.get(announcementId)
      setAnnouncement(response.data)
    } catch (error) {
      console.error('Error loading announcement details:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadAnnouncementStats = async () => {
    try {
      const response = await announcementApi.getStats(announcementId)
      setStats(response.data)
    } catch (error) {
      console.error('Error loading announcement stats:', error)
    }
  }

  const loadRecipients = async () => {
    try {
      setRecipientsLoading(true)
      const params = {
        pageNumber: recipientPage,
        pageSize: 10,
        ...(recipientFilters.isRead !== '' && { isRead: recipientFilters.isRead === 'true' }),
        ...(recipientFilters.isAcknowledged !== '' && { isAcknowledged: recipientFilters.isAcknowledged === 'true' })
      }
      
      const response = await announcementApi.getRecipients(announcementId, params)
      setRecipients(response.data.items || [])
      setRecipientTotalPages(response.data.totalPages || 1)
    } catch (error) {
      console.error('Error loading recipients:', error)
    } finally {
      setRecipientsLoading(false)
    }
  }

  const handleDelete = async () => {
    try {
      await announcementApi.delete(announcementId)
      navigate('/announcements')
    } catch (error) {
      console.error('Error deleting announcement:', error)
      showError(error)
    }
  }

  const handleUpdateRecipientStatus = async (userId, isRead, isAcknowledged) => {
    try {
      const params = {}
      if (isRead !== undefined) params.isRead = isRead
      if (isAcknowledged !== undefined) params.isAcknowledged = isAcknowledged
      
      await announcementApi.updateRecipientStatus(announcementId, userId, params)
      loadRecipients()
      loadAnnouncementStats()
    } catch (error) {
      console.error('Error updating recipient status:', error)
      showError(error)
    }
  }

  const handleResendToRecipient = async (userId) => {
    try {
      await announcementApi.resendToRecipient(announcementId, userId)
      loadRecipients()
      loadAnnouncementStats()
    } catch (error) {
      console.error('Error resending to recipient:', error)
      showError(error)
    }
  }

  // Priority colors now handled by Badge component variants

  const getPriorityText = (priority) => {
    switch (priority) {
      case 1: return 'High'
      case 2: return 'Medium'
      case 3: return 'Low'
      default: return 'Unknown'
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return <LoadingSpinner />
  }

  if (!announcement) {
    return (
      <EmptyState
        icon={MessageSquare}
        title="Announcement not found"
        description="The announcement you're looking for doesn't exist."
      />
    )
  }

  return (
    <DetailPageLayout
      title="Announcement Details"
      backLink={{ label: "Back to Announcements", href: "/announcements" }}
      actions={[
        <Button
          key="edit"
          as={Link}
          to={`/announcements/${announcementId}/edit`}
          variant="primary"
          icon={<Edit className="h-4 w-4" />}
        >
          Edit
        </Button>,
        <Button
          key="delete"
          onClick={() => setShowDeleteModal(true)}
          variant="danger"
          icon={<Trash2 className="h-4 w-4" />}
        >
          Delete
        </Button>
      ]}
    >

      <Card>
        <CardHeader 
          title={announcement.title}
          action={
            <div className="flex items-center space-x-2">
              <Badge 
                variant={announcement.priority === 1 ? 'danger' : 
                         announcement.priority === 2 ? 'warning' : 'default'}
              >
                {getPriorityText(announcement.priority)}
              </Badge>
              {announcement.isDraft ? (
                <StatusBadge status="draft" />
              ) : (
                <StatusBadge status="published" />
              )}
            </div>
          }
        />

        <CardBody>
          <div className="prose max-w-none">
            <div 
              className="text-gray-700"
              dangerouslySetInnerHTML={{ __html: announcement.content }}
            />
          </div>
        </CardBody>

        <CardBody className="bg-gray-50 border-t border-gray-200">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                {formatDate(announcement.createDateTimeUtc || announcement.createdDate)}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
              <dd className="mt-1 text-sm text-gray-900 flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                {formatDate(announcement.updateDateTimeUtc || announcement.lastUpdated)}
              </dd>
            </div>
            {announcement.eventDate && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Event Date</dt>
                <dd className="mt-1 text-sm text-gray-900 flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {formatDate(announcement.eventDate)}
                </dd>
              </div>
            )}
            {announcement.scheduleDate && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Scheduled Date</dt>
                <dd className="mt-1 text-sm text-gray-900 flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {formatDate(announcement.scheduleDate)}
                </dd>
              </div>
            )}
            {announcement.announcementSendDate && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Sent Date</dt>
                <dd className="mt-1 text-sm text-gray-900 flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {formatDate(announcement.announcementSendDate)}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-sm font-medium text-gray-500">Notification Methods</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {announcement.announceType && announcement.announceType.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {announcement.announceType.map((type, index) => (
                      <Badge key={index} variant="primary">
                        {type === 1 ? 'Email' : type === 2 ? 'SMS' : type === 3 ? 'Push' : `Method ${type}`}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  'None specified'
                )}
              </dd>
            </div>
          </dl>
        </CardBody>
      </Card>

      {stats && (
        <Card>
          <CardHeader title="Statistics" />
          <CardBody>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Total Recipients</dt>
                <dd className="mt-1 text-2xl font-semibold text-gray-900">{stats.totalRecipients || 0}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Read</dt>
                <dd className="mt-1 text-2xl font-semibold text-accent-600">{stats.readCount || 0}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Unread</dt>
                <dd className="mt-1 text-2xl font-semibold text-red-600">{stats.unreadCount || 0}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Acknowledged</dt>
                <dd className="mt-1 text-2xl font-semibold text-primary-600">{stats.acknowledgedCount || 0}</dd>
              </div>
              {stats.pendingAcknowledgmentCount !== undefined && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Pending Acknowledgment</dt>
                  <dd className="mt-1 text-2xl font-semibold text-yellow-600">{stats.pendingAcknowledgmentCount}</dd>
                </div>
              )}
            </dl>
            {stats.notificationMethodCounts && (
              <div className="mt-6">
                <dt className="text-sm font-medium text-gray-500 mb-3">Notification Method Counts</dt>
                <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-3">
                  {Object.entries(stats.notificationMethodCounts).map(([method, count]) => (
                    <div key={method} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-700">{method}</span>
                      <span className="text-lg font-semibold text-gray-900">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader 
          title="Recipients"
          action={
            <div className="flex items-center space-x-4">
              <div className="flex space-x-2">
                <FormSelect
                  value={recipientFilters.isRead}
                  onChange={(e) => setRecipientFilters(prev => ({ ...prev, isRead: e.target.value }))}
                  options={[
                    { value: '', label: 'All Read Status' },
                    { value: 'true', label: 'Read' },
                    { value: 'false', label: 'Unread' }
                  ]}
                  className="text-sm"
                />
                <FormSelect
                  value={recipientFilters.isAcknowledged}
                  onChange={(e) => setRecipientFilters(prev => ({ ...prev, isAcknowledged: e.target.value }))}
                  options={[
                    { value: '', label: 'All Acknowledgment' },
                    { value: 'true', label: 'Acknowledged' },
                    { value: 'false', label: 'Not Acknowledged' }
                  ]}
                  className="text-sm"
                />
              </div>
              <Button
                onClick={loadRecipients}
                variant="ghost"
                icon={<RefreshCw className="w-4 h-4" />}
                size="small"
              >
                {recipients.length === 0 ? 'Load Recipients' : 'Refresh'}
              </Button>
            </div>
          }
        />
        <CardBody>
          {recipientsLoading ? (
            <div className="text-center py-4">
              <LoadingSpinner />
            </div>
          ) : recipients.length === 0 ? (
            <EmptyState
              title="No recipients loaded"
              description="Click 'Load Recipients' to view recipient list"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableCell header>Recipient</TableCell>
                  <TableCell header>Unit</TableCell>
                  <TableCell header>Read Status</TableCell>
                  <TableCell header>Acknowledgment</TableCell>
                  <TableCell header>Delivered</TableCell>
                  <TableCell header>Actions</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recipients.map((recipient) => (
                  <TableRow key={recipient.userId}>
                    <TableCell>
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-800">
                              {recipient.firstName?.[0]}{recipient.lastName?.[0]}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {recipient.firstName} {recipient.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {recipient.email}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {recipient.unitNumber}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={recipient.isRead ? 'read' : 'unread'} />
                    </TableCell>
                    <TableCell>
                      {announcement.isAcknowledgeReq ? (
                        <StatusBadge 
                          status={recipient.isAcknowledged ? 'acknowledged' : 'pending'} 
                        />
                      ) : (
                        <span className="text-sm text-gray-500">Not required</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {recipient.deliveredDate ? formatDate(recipient.deliveredDate) : 'Not delivered'}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        {!recipient.isRead && (
                          <Button
                            onClick={() => handleUpdateRecipientStatus(recipient.userId, true)}
                            variant="accent"
                            size="small"
                          >
                            Mark Read
                          </Button>
                        )}
                        {announcement.isAcknowledgeReq && !recipient.isAcknowledged && (
                          <Button
                            onClick={() => handleUpdateRecipientStatus(recipient.userId, undefined, true)}
                            variant="primary"
                            size="small"
                          >
                            Mark Ack
                          </Button>
                        )}
                        <Button
                          onClick={() => handleResendToRecipient(recipient.userId)}
                          variant="ghost"
                          icon={<Send className="w-3 h-3" />}
                          size="small"
                        >
                          Resend
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {recipientTotalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing page {recipientPage} of {recipientTotalPages}
                </div>
                <div className="flex space-x-2">
                  <Button
                    onClick={() => setRecipientPage(prev => Math.max(1, prev - 1))}
                    disabled={recipientPage === 1}
                    variant="secondary"
                    size="small"
                  >
                    Previous
                  </Button>
                  <Button
                    onClick={() => setRecipientPage(prev => Math.min(recipientTotalPages, prev + 1))}
                    disabled={recipientPage === recipientTotalPages}
                    variant="secondary"
                    size="small"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Announcement"
        message="Are you sure you want to delete this announcement? This action cannot be undone."
        confirmText="Delete"
        confirmVariant="danger"
      />
    </DetailPageLayout>
  )
}

export default AnnouncementDetail